import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectOrganisationList } from '../../../redux/selectors/organisation';
import { selectCurrentPartner } from '../../../redux/selectors/authentication';
import { doReadOrganisationList, doDeleteOrganisation } from '../../../redux/thunks/organisation';
import { doOrganisationListReset } from '../../../redux/actionCreators/organisation';
import { Search } from '../../../utils/Search';
// table config
import tableConfig from './tableConfig';

// components
import Table from '../../../components/Table/Table';
import SquareDelete from '../../../components/SquareDelete/SquareDelete';

import routes from '../../../constants/routes';
import MobileTable from '../../../components/MobileTable/MobileTable';
import useIsMobile from '../../../hooks/useIsMobile';
import SDialog from '../../../components/SDialog/SDialog';
import Typography from '../../../components/Typography/Typography';
import SButton from '../../../components/SButton/SButton';
import FlexRow from '../../../components/FlexBox/FlexRow';
import { reverse } from 'named-urls';

const CustomerList = () => {

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { currentPartner, organisationList } = useSelector((state) => ({
    currentPartner: selectCurrentPartner(state),
    organisationList: selectOrganisationList(state),
  }));
  const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState(false);
  const [customer, setCustomer] = useState(null);

  const [filters, setFilters] = useState({ name: '', id: '' });
  const [pagination, setPagination] = useState({ total: 0, currentPage: 1, perPage: 25 });

  const history = useHistory();

  useEffect(() => {
    if (currentPartner) dispatch(doReadOrganisationList(currentPartner?.partnerId));
    return () => {
      dispatch(doOrganisationListReset());
    };
  }, [dispatch, currentPartner]);

  const togglePopup = (popupName) => {
    if (popupName === 'DeleteCustomer') {
      setShowDeleteCustomerModal(!showDeleteCustomerModal);
      if (showDeleteCustomerModal) {
        setCustomer(null);
      }
    }
  };

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: organisationList?.organisations?.length
        ? organisationList?.organisations?.length
        : 0
    }));
  }, [organisationList?.organisations?.length, setPagination]);

  const delete_column = [
    {
      title: 'Action',
      render: (columnProps, row) => {
        return (
          <SquareDelete
            onPress={() => {
              setCustomer(row);
              togglePopup('DeleteCustomer');
            }}
            style={{ height: '2rem', width: '2rem', padding: '0' }}
          />
        );
      },
      renderM: (columnProps, row) => {
        return (
          <SquareDelete
            style={{ height: '2rem', width: '2rem', padding: '0' }}
            onPress={() => {
              setCustomer(row);
              togglePopup('DeleteCustomer');
            }}
          />
        );
      },
    },
  ];

  const handleDeleteOrganisation = useCallback(
    (event) => {
      dispatch(
        doDeleteOrganisation(
          {
            partnerId: currentPartner?.partnerId,
            organisationId: customer?.id,
          },
          (status) => {
            if (status === 'success') {
            }
          },
        ),
      );
      setCustomer(null);
      setShowDeleteCustomerModal(false);
    },
    [currentPartner?.partnerId, customer?.id, dispatch],
  );

  const filteredData = useCallback(() => {
    let data = organisationList?.organisations ? organisationList?.organisations : [];
    if (filters.id !== '') {
      data = Search(data, 'id', filters.id);
    }
    if (filters.name !== '') {
      data = Search(data, 'name', filters.name);
    }

    const sortedOrganizations = [...data].sort((a, b) => b.id - a.id);
    if (!isMobile) {
      data = sortedOrganizations.slice(
        (pagination.currentPage - 1) * 7,
        pagination.currentPage * pagination.perPage,
      );
    } else {
      data = sortedOrganizations;
    }

    return data;
  }, [
    filters.id,
    filters.name,
    organisationList?.organisations,
    pagination.currentPage,
    pagination.perPage,
    isMobile
  ]);


  const mobileFiltersConfig = useMemo(() => {
    return [{ name: "Search by Customer Name", key: "name", type: "search" }, { name: "Search by ID", key: "id", type: "search" }]
  }, [])

  const hSearchChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const search = {
    onChange: hSearchChange,
  };

  const changePage = (p) => {
    const { page, per_page } = p;
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: per_page,
    }))
  };

  const handleRedirect = (customerId) => {
    const url = reverse(`${routes.customer.customerId.details}`, {
      customerId: customerId || 1,
    });
    history.push(url);
  };

  return (
    <Fragment>
      <div className="spacer__0-5" />
      <div>
        <div>
          {showDeleteCustomerModal && (
            <SDialog
              title='Delete Customer'
              open={showDeleteCustomerModal}
              onClose={() => {
                setShowDeleteCustomerModal(false);
              }}
              titleConfig={{
                variant: isMobile ? 'h6M' : 'h5M'
              }}
              secondaryButtonConfig={{
                text: 'Cancel',
                onClick: () => {
                  setShowDeleteCustomerModal(false);
                }
              }}
              primaryButtonConfig={{
                text: 'Delete',
                color: 'error',
                onClick: () => { handleDeleteOrganisation() }
              }}
            >
              <Typography variant={isMobile ? "b2M" : "b1M"} style={{ padding: '1.5rem 0' }}> Are you sure you want to delete {customer ? customer.name : ''} ? This action is non
                reversible and will cause you to lose all data associated with this customer/ s</Typography>
            </SDialog>
          )}

          <FlexRow justifyContent='space-between' styles={{ marginBottom: '1rem' }}>
            <Typography variant={"h7M"}>Customer</Typography>
            <SButton
              onClick={() => {
                history.push(routes.customer.addNew);
              }}
              minWidth
            >
              Add Customer
            </SButton>
          </FlexRow>
          <div>
            {isMobile ?
              <MobileTable
                columns={tableConfig}
                data={filteredData()}
                filtersConfig={mobileFiltersConfig}
                filters={filters}
                setFilters={setFilters}
                onCardClick={(d) => { handleRedirect(d?.id) }}
                actions={delete_column} /> :
              <Table
                columns={[...tableConfig, ...delete_column]}
                // data={(!!organisationList && organisationList.organisations) || []}
                data={filteredData()}
                pagination={pagination}
                options={{ search }}
                changePage={changePage}
              />
            }
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CustomerList;
